import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getFormWithData, postFormData } from "../../../services/restAPI/FormsV3";
import { renderTableInlineField, SUPPORTED_INLINE_EDIT_TYPES } from "./renderTableInlineField";
import { Message } from "../Messages/Messages";
import { useFocusLock } from "./useFocusLock";

export const getRecordId = (item) => item?.UID ?? item?.ID ?? item?.uid ?? item?.id ?? null;

const isIdLike = (name = "") => {
   const lower = String(name).toLowerCase();
   return lower === "id" || lower === "_id" || lower.includes("uuid");
};

const FOCUSABLE_SELECTOR =
   'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([aria-disabled="true"]), [type="checkbox"]:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isEditableColumn = (col, formId, resolvedType) => {
   if (!formId || !col) return false;
   if (col?.id === "api_recordActions") return false;
   if (isIdLike(col?.techName) || isIdLike(col?.label)) return false;
   if (resolvedType != null && !SUPPORTED_INLINE_EDIT_TYPES.has(resolvedType)) return false;
   return true;
};

const getCellTypedValue = (item, col) => {
   if (!item || !col) return "";
   return item?.[col?.dataKey ?? col?.techName ?? col?.label] ?? "";
};

const getCellRawValue = (item, col) => {
   const value = getCellTypedValue(item, col);
   return value == null ? "" : String(value);
};

const extractValueFromTarget = (t) => {
   try {
      if (!t) return undefined;
      if (t.tagName === "SELECT") return t.value;
      const role = t.getAttribute?.("role");
      if (role === "option" || role === "menuitem") {
         const dataValue = t.getAttribute("data-value");
         if (dataValue != null) return dataValue;
         if (t.textContent) return t.textContent.trim();
      }
      const withDataValue = t.closest?.("[data-value]");
      if (withDataValue) {
         const dataValue = withDataValue.getAttribute("data-value");
         if (dataValue != null) return dataValue;
         if (withDataValue.textContent) return withDataValue.textContent.trim();
      }
      return undefined;
   } catch {
      return undefined;
   }
};

const clearAllTablesFocusAcrossDocument = ({ except = null } = {}) => {
   Array.from(document.querySelectorAll('[data-table-widget="true"]')).forEach((tbl) => {
      if (except && tbl === except) return;
      tbl.querySelectorAll(".bp2-active-cell").forEach((el) =>
         el.classList.remove("bp2-active-cell"),
      );
      tbl.querySelectorAll('[data-grid-cell="true"][tabindex="0"]').forEach((el) =>
         el.setAttribute("tabindex", "-1"),
      );
      tbl.setAttribute("data-bp2-grid-active", "false");
   });
};

export const useTableInlineEdit = ({
   formId,
   listId,
   rows,
   setRows,
   visibleColumns,
   tableRootRef,
   navRef,
   isInAllowedPortal,
   intl,
   formV3Ctx,
   editedValuesRef,
}) => {
   const [editing, setEditing] = useState(null);
   const [saving, setSaving] = useState(false);
   const editingHostRef = useRef(null);
   const lockIdRef = useRef(Symbol("bp2-edit-lock"));
   const didInitialSelectRef = useRef(false);
   const lastEditPosRef = useRef({ row: null, col: null, recordId: null });
   const pendingFocusRef = useRef(null);
   const enterStartSuppressedRef = useRef(false);
   const fieldTypeByIdRef = useRef({});

   useEffect(() => {
      window.__gridEditing = !!editing;
      return () => {
         window.__gridEditing = false;
      };
   }, [editing]);

   useEffect(() => {
      if (!formId) return;
      let cancelled = false;
      getFormWithData(formId)
         .then((res) => {
            if (cancelled) return;
            const map = {};
            for (const field of res?.data?.fields || []) {
               if (field.id && field.type) map[field.id] = field.type;
            }
            fieldTypeByIdRef.current = map;
         })
         .catch(() => {});
      return () => {
         cancelled = true;
      };
   }, [formId]);

   const toDisplayValue = useCallback(
      (typedValue, col) => {
         const resolvedType = fieldTypeByIdRef.current[col?.techName] || col?.type || "String";
         if (resolvedType === "Boolean") {
            return typedValue
               ? intl.formatMessage({ id: "global.yes" })
               : intl.formatMessage({ id: "global.no" });
         }
         if (typedValue && typeof typedValue === "object" && "label" in typedValue) {
            return typedValue.label;
         }
         return typedValue == null ? "" : String(typedValue);
      },
      [intl],
   );

   const focusEditableControl = useCallback(
      (rowIndex, colIndex) => {
         let tries = 0;
         const tick = () => {
            const root = tableRootRef.current;
            if (!root) return;
            const cell = root.querySelector(
               `[data-grid-cell="true"][data-row="${rowIndex}"][data-col="${colIndex}"]`,
            );
            if (cell) {
               const el = cell.querySelector(FOCUSABLE_SELECTOR) || cell;
               if (el && typeof el.focus === "function") {
                  try {
                     el.focus({ preventScroll: true });
                  } catch {
                     el.focus();
                  }
                  return;
               }
            }
            if (++tries < 24) requestAnimationFrame(tick);
         };
         requestAnimationFrame(tick);
      },
      [tableRootRef],
   );

   const restoreFocusToEditedCell = useCallback(() => {
      const { row, col, recordId } = lastEditPosRef.current ?? {};
      if (row == null || col == null) return;
      pendingFocusRef.current = { row, col, recordId };
      try {
         navRef.current?.setActive?.({ row, col });
         navRef.current?.focusCell?.(row, col);
      } catch {}
   }, [navRef]);

   // Restore focus after data reload (e.g. after inline edit commit triggers listReloadRequest)
   useEffect(() => {
      const pending = pendingFocusRef.current;
      if (!pending || !rows?.nodes?.length) return;
      pendingFocusRef.current = null;
      const { col, recordId, row: fallbackRow } = pending;
      const nodes = rows.nodes;
      const row =
         recordId != null ? nodes.findIndex((node) => getRecordId(node) === recordId) : fallbackRow;
      if (row == null || row < 0) return;
      requestAnimationFrame(() => {
         navRef.current?.setActive?.({ row, col });
         navRef.current?.focusCell?.(row, col);
      });
   }, [rows, navRef]); // eslint-disable-line react-hooks/exhaustive-deps

   const startEditAt = useCallback(
      (item, rowIndex, colIndex) => {
         const col = visibleColumns[colIndex];
         if (!isEditableColumn(col, formId)) return;
         const resolvedType = fieldTypeByIdRef.current[col?.techName] || col?.type || "String";
         if (!SUPPORTED_INLINE_EDIT_TYPES.has(resolvedType)) {
            Message.warning(
               intl.formatMessage({
                  id: "table.inlineEdit.typeNotSupported",
                  defaultMessage: "This field type cannot be edited inline.",
               }),
            );
            return;
         }
         const rawValue = getCellTypedValue(item, col);
         const value =
            resolvedType === "Boolean" && typeof rawValue === "string"
               ? rawValue === intl.formatMessage({ id: "global.yes" })
               : (rawValue ?? "");
         const fieldId = `${String(listId)}-${String(col?.id ?? col?.techName ?? "")}`;

         setEditing({
            row: rowIndex,
            col: colIndex,
            techName: col?.techName || col?.label,
            value,
            fieldId,
         });

         lastEditPosRef.current = { row: rowIndex, col: colIndex, recordId: getRecordId(item) };
         requestAnimationFrame(() => focusEditableControl(rowIndex, colIndex));
      },
      [visibleColumns, formId, listId, intl, focusEditableControl],
   );

   const buildPayloadForRow = useCallback(
      async (rowIndex, editedTechName, editedValue) => {
         const item = rows?.nodes?.[rowIndex] || {};
         const recordId = getRecordId(item);
         const result = await getFormWithData(formId, recordId);

         let editedVal = editedValue;
         if (editedVal !== null && typeof editedVal === "object" && "value" in editedVal) {
            editedVal = editedVal.value;
         }

         const prepareData = {};
         for (const field of result?.data?.fields || []) {
            const objectValue = field?.value?.originalValue;
            const originalVal =
               objectValue && typeof objectValue === "object" && "value" in objectValue
                  ? objectValue.value
                  : objectValue;
            const isEdited = field.id === editedTechName || field.techName === editedTechName;
            prepareData[field.id] =
               isEdited && editedVal !== undefined ? editedVal : (originalVal ?? null);
         }

         return prepareData;
      },
      [rows, formId],
   );

   const cancelEdit = useCallback(() => {
      setEditing(null);
      editingHostRef.current = null;
      enterStartSuppressedRef.current = false;
      didInitialSelectRef.current = false;
      requestAnimationFrame(restoreFocusToEditedCell);
   }, [restoreFocusToEditedCell]);

   const commitEdit = useCallback(async () => {
      if (!editing || saving) return;
      const { row, techName, value, col: colIndex } = editing;
      const displayCol = visibleColumns[colIndex];
      const displayKey = displayCol?.dataKey ?? displayCol?.label ?? techName;
      const displayValue = toDisplayValue(value, displayCol);
      let previousValue;

      setRows((prev) => {
         if (!prev?.nodes) return prev;
         previousValue = getCellRawValue(prev.nodes[row], displayCol);
         return {
            ...prev,
            nodes: prev.nodes.map((item, idx) =>
               idx === row ? { ...item, [displayKey]: displayValue } : item,
            ),
         };
      });

      try {
         setSaving(true);
         const item = rows?.nodes?.[row];
         const recordId = getRecordId(item);
         const payload = await buildPayloadForRow(row, techName, value);
         await postFormData(formId, payload);
         if (recordId != null) {
            editedValuesRef.current[recordId] = {
               ...(editedValuesRef.current[recordId] || {}),
               [displayKey]: displayValue,
            };
         }
      } catch {
         setRows((prev) => {
            if (!prev?.nodes) return prev;
            return {
               ...prev,
               nodes: prev.nodes.map((item, idx) =>
                  idx === row ? { ...item, [displayKey]: previousValue } : item,
               ),
            };
         });
      } finally {
         setSaving(false);
         setEditing(null);
         enterStartSuppressedRef.current = false;
         requestAnimationFrame(restoreFocusToEditedCell);
         editingHostRef.current = null;
      }
   }, [
      editing,
      saving,
      buildPayloadForRow,
      formId,
      visibleColumns,
      toDisplayValue,
      restoreFocusToEditedCell,
   ]); // setRows i editedValuesRef są stabilne (setter stanu i ref)

   const handleEnterEdit = useCallback(
      ({ row, col }) => {
         if (enterStartSuppressedRef.current) {
            enterStartSuppressedRef.current = false;
            return;
         }
         const item = rows?.nodes?.[row];
         if (!item) return;
         startEditAt(item, row, col);
      },
      [rows, startEditAt],
   );

   // Track select/dropdown value changes during edit
   useEffect(() => {
      if (!editing) return;
      const root = tableRootRef.current;
      if (!root) return;
      const { row, col } = editing;

      const getCellControl = () => {
         const cell = root.querySelector(
            `[data-grid-cell="true"][data-row="${row}"][data-col="${col}"]`,
         );
         if (!cell) return null;
         return (
            cell.querySelector(
               'select:not([disabled]), [role="combobox"]:not([aria-disabled="true"])',
            ) || null
         );
      };

      const setNextValue = (nextVal) => {
         if (nextVal == null) return;
         setEditing((prev) => {
            if (!prev) return prev;
            const value = typeof nextVal === "string" ? nextVal : String(nextVal);
            return value === prev.value ? prev : { ...prev, value };
         });
      };

      const onDocChange = (e) => {
         const target = e.target;
         if (!(target instanceof Element) || target.tagName !== "SELECT") return;
         const value = extractValueFromTarget(target);
         if (value != null) setNextValue(value);
      };

      const extractFromDropdownTarget = (t) => {
         if (!(t instanceof Element)) return null;
         const item =
            t.closest("[data-value]") ||
            t.closest('[role="option"]') ||
            t.closest('[role="menuitem"]');
         if (!item) return null;
         const next = extractValueFromTarget(item) ?? extractValueFromTarget(getCellControl());
         return next ?? null;
      };

      const onDocClick = (e) => {
         const next = extractFromDropdownTarget(e.target);
         if (next != null) setNextValue(next);
      };

      const onDocKeyDown = (e) => {
         if (e.key !== "Enter" && e.key !== " ") return;
         const next = extractFromDropdownTarget(e.target);
         if (next != null) requestAnimationFrame(() => setNextValue(next));
      };

      document.addEventListener("change", onDocChange, true);
      document.addEventListener("click", onDocClick, true);
      document.addEventListener("keydown", onDocKeyDown, true);
      return () => {
         document.removeEventListener("change", onDocChange, true);
         document.removeEventListener("click", onDocClick, true);
         document.removeEventListener("keydown", onDocKeyDown, true);
      };
   }, [editing, tableRootRef]);

   // __editLock for focus management during editing
   useEffect(() => {
      if (editing) {
         window.__editLock = {
            id: lockIdRef.current,
            cancel: () => cancelEdit(),
            focus: () => {
               const host = editingHostRef.current;
               if (!host) return;
               const probe =
                  host.querySelector('input:not([type="hidden"]):not([disabled])') ||
                  host.querySelector("textarea") ||
                  host.querySelector(".ant-select-selection-search-input") ||
                  host.querySelector(".ant-input") ||
                  host.querySelector(".ant-picker-input input") ||
                  host.querySelector('[tabindex]:not([tabindex="-1"])') ||
                  host;
               if (probe?.focus) {
                  try {
                     probe.focus({ preventScroll: true });
                     if (!didInitialSelectRef.current && typeof probe.select === "function") {
                        probe.select();
                        didInitialSelectRef.current = true;
                     }
                  } catch {
                     probe.focus();
                  }
               }
            },
         };
         setTimeout(() => window.__editLock?.focus?.(), 0);
      } else if (window.__editLock?.id === lockIdRef.current) {
         delete window.__editLock;
      }
      return () => {
         if (window.__editLock?.id === lockIdRef.current) delete window.__editLock;
      };
   }, [editing, cancelEdit]);

   useFocusLock({ editing, tableRootRef, editingHostRef, isInAllowedPortal, didInitialSelectRef });

   // When not editing: clicking/focusing another table clears focus in all others
   useEffect(() => {
      if (editing) return;
      const onDocPointerDown = (e) => {
         const targetTable = e.target?.closest?.('[data-table-widget="true"]');
         if (!targetTable) return;
         clearAllTablesFocusAcrossDocument({ except: targetTable });
      };
      const onDocFocusIn = (e) => {
         const targetTable = e.target?.closest?.('[data-table-widget="true"]');
         if (!targetTable) return;
         clearAllTablesFocusAcrossDocument({ except: targetTable });
      };
      document.addEventListener("pointerdown", onDocPointerDown, true);
      document.addEventListener("focusin", onDocFocusIn, true);
      return () => {
         document.removeEventListener("pointerdown", onDocPointerDown, true);
         document.removeEventListener("focusin", onDocFocusIn, true);
      };
   }, [editing]);

   const decoratedColumns = useMemo(() => {
      const indexOfRow = (item) => (rows?.nodes ? rows.nodes.indexOf(item) : -1);

      return (visibleColumns ?? []).map((col, colIndex) => {
         const base =
            col.renderCell ||
            col.render ||
            ((item) => String(item?.[col?.techName] ?? item?.[col?.label] ?? ""));

         const withGridCell = (item) => {
            const rowIndex = indexOfRow(item);
            const isThisEditing =
               editing &&
               editing.row === rowIndex &&
               editing.col === colIndex &&
               editing.techName === (col?.techName || col?.label);
            const editableHere = isEditableColumn(col, formId);

            if (!isThisEditing) {
               return (
                  <span
                     {...navRef.current?.getCellProps(rowIndex, colIndex)}
                     className="bp2-cell-focus-target"
                     style={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                        minHeight: "1em",
                        minWidth: 0,
                        outline: "none",
                     }}
                     onKeyDown={(e) => {
                        if (e.key === "Enter" && editableHere) {
                           e.preventDefault();
                           e.stopPropagation();
                           startEditAt(item, rowIndex, colIndex);
                        }
                     }}
                     onMouseDown={(e) => {
                        if (editing && (editing.row !== rowIndex || editing.col !== colIndex)) {
                           enterStartSuppressedRef.current = true;
                           e.preventDefault();
                           e.stopPropagation();
                        }
                     }}
                  >
                     {base(item)}
                  </span>
               );
            }

            // prepareRows converts booleans to localized strings; convert back so
            // FormV3CheckboxField receives a real boolean instead of a truthy string.
            const resolvedType = fieldTypeByIdRef.current[col?.techName] || col?.type || "String";
            const rawValue = getCellTypedValue(item, col);
            const fieldValue =
               resolvedType === "Boolean" && typeof rawValue === "string"
                  ? rawValue === intl.formatMessage({ id: "global.yes" })
                  : (rawValue ?? "");
            const field = {
               type: resolvedType,
               id: `${String(listId)}-${String(col?.id)}-${String(getRecordId(item))}`,
               label: col?.id,
               modelField: { multiplicity: "SINGULAR" },
               dataUrl: col?.dataUrl ?? "",
               techName: col?.techName,
               value: fieldValue,
            };
            const onValueChange = ({ value: next }) => {
               setEditing((prev) => (prev ? { ...prev, value: next } : prev));
            };

            return (
               <span
                  {...navRef.current?.getCellProps(rowIndex, colIndex)}
                  ref={(el) => {
                     editingHostRef.current = el;
                  }}
                  tabIndex={-1}
                  onFocus={() => {}}
                  onBlurCapture={(e) => {
                     const host = editingHostRef.current;
                     const to = e.relatedTarget;
                     if (!host) return;
                     if (to && (host.contains(to) || isInAllowedPortal(to))) return;
                     e.preventDefault();
                     e.stopPropagation();
                     Promise.resolve().then(() => window.__editLock?.focus?.());
                  }}
                  className="bp2-cell-focus-target"
                  style={{
                     display: "block",
                     width: "100%",
                     height: "100%",
                     minWidth: 0,
                     outline: "none",
                     textAlign: "center",
                  }}
                  onKeyDownCapture={(e) => {
                     if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        void commitEdit();
                     } else if (e.key === "Escape") {
                        e.preventDefault();
                        e.stopPropagation();
                        cancelEdit();
                     }
                  }}
               >
                  {formV3Ctx ? (
                     <span
                        onInput={(e) => {
                           const next = (e?.target && "value" in e.target && e.target.value) || "";
                           setEditing((prev) => (prev ? { ...prev, value: next } : prev));
                        }}
                     >
                        {renderTableInlineField(field, formId, false, onValueChange, intl)}
                     </span>
                  ) : null}
               </span>
            );
         };

         return { ...col, renderCell: withGridCell };
      });
   }, [
      visibleColumns,
      rows,
      editing,
      formId,
      listId,
      startEditAt,
      commitEdit,
      cancelEdit,
      formV3Ctx,
      isInAllowedPortal,
      intl,
   ]); // navRef, editingHostRef, enterStartSuppressedRef are refs — stable, read at render time

   return {
      editing,
      saving,
      handleEnterEdit,
      decoratedColumns,
   };
};
